import { ActionTypes, ActivityTypes } from "../constants/action-types";
import { fromJS } from "immutable";
import { handleActions } from "redux-actions";
import { getUserTimeZone } from "../api/api";
import { isValid, format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { buildTimeValues } from "../activities/CalendarTimeline";

const today = new Date();
const todayDateString = format(today, "yyyy-MM-dd");
const timelines = fromJS(buildTimeValues()).sortBy((item) => item.get("distanceToNow"));
const sortedTimelines = timelines.filter((item) => item.get("distanceToNow") >= 0);

export const defaultActivity = {
  id: null,
  itemId: null,
  itemType: "SALE",
  event: {
    data: {
      activityType: ActivityTypes.meeting,
      contactId: "",
      assignees: [],
      summary: "Meeting",
      attendees: [],
      organizer: "",
      location: "",
      description: "",
      privateDescription: "",
      calendarId: "primary",
      start: todayDateString,
      startTime: sortedTimelines.getIn([0, "value"]),
      end: todayDateString,
      endTime: sortedTimelines.getIn([2, "value"]),
      timeZone: getUserTimeZone(),
      reminderEnable: false,
      hasGoogleCalendar: false,
      hasGmail: false,
      hasOutlook: false,
      tracking: true,
      sendBulkEmail: false,
      done: false,
      visibility: "Shared",
      emailReminderBeforeInMinutes: 30,
      popupReminderBeforeInMinutes: 10,
      itemIds: "",
      item: {},
    },
  },
};

export const defaultEmail = {
  id: null,
  itemId: null,
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  body: "",
  provider: "", // google or azure
  // Other field
  item: {},
  tracking: true,
  visibility: "Shared",
};

export const defaultUpcomingFilters = {
  activityType: {
    operator: "is",
    dataType: "string",
    values: ["task"],
  },
};

const initialState = fromJS({
  show: false,
  showChrome: false,
  activities: [],
  activity: defaultActivity,
  myOverDueActivityNum: 0,
  upcomingFilters: {},
  upcoming: [],
  showEmail: false,
  email: defaultEmail,
});

export const actions = {
  [ActionTypes.searchActivities]: {
    next(state, data) {
      const { payload, error, pending } = data;
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        activities: payload,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.countMyDueOverActivities]: {
    next(state, data) {
      const { payload, error, pending } = data;
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        myOverDueActivityNum: payload,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.createActivity]: {
    next(state, data) {
      const { error, pending } = data;
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        activity: defaultActivity,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeActivityData]: {
    next(state, { payload }) {
      const { fieldId, fieldValue } = payload;
      const newState = {
        ...state.toJS(),
        activity: {
          ...state.get("activity").toJS(),
          event: {
            ...state.getIn(["activity", "event"]).toJS(),
            data: {
              ...state.getIn(["activity", "event", "data"]).toJS(),
              [fieldId]: fieldValue,
            },
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.showActivityDialog]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const activityData = fromJS(payload || {});
      // Fix error date
      const timeZone = activityData.getIn(["event", "data", "timeZone"]);
      let start = activityData.getIn(["event", "data", "start"]);
      if (typeof start === "number") {
        start = formatInTimeZone(
          start,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      let startTime = activityData.getIn(["event", "data", "startTime"]);
      const startDateTime = new Date(`${start}T${startTime}`);
      if (!isValid(startDateTime)) {
        startTime = sortedTimelines.getIn([0, "value"]);
      }
      let end = activityData.getIn(["event", "data", "end"]);
      if (typeof end === "number") {
        end = formatInTimeZone(
          end,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      let endTime = activityData.getIn(["event", "data", "endTime"]);
      const endDateTime = new Date(`${end}T${endTime}`);
      if (!isValid(endDateTime)) {
        endTime = sortedTimelines.getIn([2, "value"]);
      }
      const newState = {
        ...state.toJS(),
        show: true,
        activity: {
          ...activityData.toJS(),
          event: {
            ...(activityData.get("event")?.toJS() || {}),
            data: {
              ...(activityData.getIn(["event", "data"])?.toJS() || {}),
              start,
              startTime,
              end,
              endTime,
            },
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.hideActivityDialog]: {
    next(state) {
      const newState = {
        ...state.toJS(),
        show: false,
        activity: defaultActivity,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.resetActivityDialog]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        activity: {
          ...defaultActivity,
          event: {
            ...defaultActivity.event,
            data: {
              ...defaultActivity.event.data,
              ...payload.data,
            },
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeFiltersUpcomingChrome]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        upcomingFilters: payload.data,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.searchUpcomingChrome]: {
    next(state, data) {
      const { payload, error, pending } = data;
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        upcoming: payload,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.showActivityDialogChrome]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const activityData = fromJS(payload || {});
      // Fix error date
      const timeZone = activityData.getIn(["event", "data", "timeZone"]);
      let start = activityData.getIn(["event", "data", "start"]);
      if (typeof start === "number") {
        start = formatInTimeZone(
          start,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      let startTime = activityData.getIn(["event", "data", "startTime"]);
      const startDateTime = new Date(`${start}T${startTime}`);
      if (!isValid(startDateTime)) {
        startTime = sortedTimelines.getIn([0, "value"]);
      }
      let end = activityData.getIn(["event", "data", "end"]);
      if (typeof end === "number") {
        end = formatInTimeZone(
          end,
          timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          "yyyy-MM-dd"
        );
      }
      let endTime = activityData.getIn(["event", "data", "endTime"]);
      const endDateTime = new Date(`${end}T${endTime}`);
      if (!isValid(endDateTime)) {
        endTime = sortedTimelines.getIn([2, "value"]);
      }
      const newState = {
        ...state.toJS(),
        showChrome: true,
        activity: {
          ...activityData.toJS(),
          event: {
            ...(activityData.get("event")?.toJS() || {}),
            data: {
              ...(activityData.getIn(["event", "data"])?.toJS() || {}),
              start,
              startTime,
              end,
              endTime,
            },
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.hideActivityDialogChrome]: {
    next(state) {
      const newState = {
        ...state.toJS(),
        showChrome: false,
        activity: defaultActivity,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeEmailData]: {
    next(state, { payload }) {
      const { fieldId, fieldValue } = payload;
      const newState = {
        ...state.toJS(),
        email: {
          ...state.get("email").toJS(),
          [fieldId]: fieldValue,
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.showEmailDialog]: {
    next(state, { payload }) {
      if (payload === null) {
        return state;
      }
      const emailData = fromJS(payload || {});
      let emailStatus = emailData.get("emailStatus");
      if (!emailStatus) {
        emailStatus = emailData.get("status");
      }
      const newState = {
        ...state.toJS(),
        showEmail: true,
        email: {
          ...emailData.toJS(),
          emailStatus,
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.hideEmailDialog]: {
    next(state) {
      const newState = {
        ...state.toJS(),
        showEmail: false,
        email: defaultEmail,
      };
      return fromJS(newState);
    },
  },
};

export const activityReducer = handleActions(actions, initialState);
